import { useScroll } from 'ahooks'
import { Layout } from 'antd'
import classNames from 'classnames'
import * as React from 'react'
import { useIntercom } from 'react-use-intercom'

import { HeaderMenu } from '../HeaderMenu'
import { SiderMenu } from '../SiderMenu'
import { RouteProtection } from '@/components/ui/RouteProtection'
import {
  StatusAlert,
  WorkspaceCustomerSuccessAlert,
} from '@/components/workspace/WorkspaceCustomerSuccessAlert'
import { WorkspaceStatusAlert } from '@/components/workspace/WorkspaceStatusAlert'
import { WorkspaceUsageAlert } from '@/components/workspace/WorkspaceUsageAlert'
import { useThemeContext } from '@/context/ThemeContext'
import { useWorkspaceContext } from '@/context/WorkspaceContext'
import { WorkspaceAlerts } from '@/utils/workspaces'

import styles from './BasicLayout.module.css'

const { Content, Header } = Layout

export type BasicLayoutProps = {
  baseUrl: string
  children: React.ReactNode
  className?: string
}

export function BasicLayout({
  baseUrl,
  children,
  className,
}: BasicLayoutProps): JSX.Element {
  const target = React.useRef(null)
  const scrollPosition = useScroll(target?.current?.parentNode)
  const { boot } = useIntercom()
  const { isLightMode, isDarkMode } = useThemeContext()
  const { workspace, user, membership } = useWorkspaceContext()
  const showWorkspaceCustomerSuccessAlert =
    typeof workspace?.alert_status !== 'undefined' &&
    workspace?.alert_status !== WorkspaceAlerts.NO_ALERT

  const layoutClassName = classNames(className, styles['basicLayout'], {
    [styles['has-scrolled-light']]: scrollPosition?.top > 20 && isLightMode,
    [styles['has-scrolled-dark']]: scrollPosition?.top > 20 && isDarkMode,
    'cl-dark-theme': isDarkMode,
    'cl-light-theme': isLightMode,
  })

  if (user && workspace && membership) {
    boot({
      company: {
        companyId: workspace.id,
        name: workspace.name,
        website: workspace.domain,
        customAttributes: {
          'workspace-activation': workspace?.origin_referral?.status,
          'workspace-status': workspace.status,
          ...Object.entries(workspace.descriptor).reduce(
            (acc, [key, value]) => {
              acc[`workspace-${key}`] = value
              return acc
            },
            {}
          ),
        },
      },
      customAttributes: {
        role: membership.role,
        username: user.username,
        'webui-enabled': user?.web_ui_enabled,
      },
      email: user.email,
      name: user.name,
      userId: user.id,
      userHash: user.hash,
    })
  }

  if (workspace) {
    const workspaceStatus = workspace?.status

    const workspaceStatusAlert =
      workspaceStatus === 'EXPIRED' ||
      workspaceStatus === 'PAUSED' ||
      workspaceStatus === 'INACTIVE'

    if (workspaceStatusAlert) {
      return <WorkspaceStatusAlert status={workspaceStatus} />
    }
  }
  return (
    <Layout className={layoutClassName}>
      <WorkspaceUsageAlert />
      {showWorkspaceCustomerSuccessAlert && (
        <WorkspaceCustomerSuccessAlert
          status={workspace?.alert_status as StatusAlert}
        />
      )}
      <Layout>
        <SiderMenu baseUrl={baseUrl} className={styles['layout-sider']} />
        <Layout className={styles['layout-main']}>
          <Header className={styles['layout-header']}>
            <HeaderMenu />
          </Header>
          <RouteProtection>
            <Content id="main-content" className={styles['layout-content']}>
              <div
                className={classNames(
                  styles['layout-content-wrapper'],
                  'cl-flex-full-page'
                )}
                ref={target}
              >
                {children}
              </div>
            </Content>
          </RouteProtection>
        </Layout>
      </Layout>
    </Layout>
  )
}
